import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Search, MapPin, Briefcase, Clock, Building2, ChevronRight, Users } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CareersPage = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    location: '',
    work_mode: '',
    job_type: '',
    department: ''
  });
  const [filterOptions, setFilterOptions] = useState({
    work_modes: [],
    job_types: [],
    departments: [],
    locations: []
  });
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 10,
    total: 0,
    total_pages: 0
  });

  useEffect(() => {
    fetchFilterOptions();
    fetchJobs();
  }, []);

  useEffect(() => {
    fetchJobs();
  }, [filters, pagination.page]);

  const fetchFilterOptions = async () => {
    try {
      const response = await axios.get(`${API}/careers/filters`);
      setFilterOptions(response.data);
    } catch (error) {
      console.error('Failed to fetch filter options:', error);
    }
  };

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', pagination.page);
      params.append('per_page', pagination.per_page);
      
      if (filters.search) params.append('search', filters.search);
      if (filters.location) params.append('location', filters.location);
      if (filters.work_mode) params.append('work_mode', filters.work_mode);
      if (filters.job_type) params.append('job_type', filters.job_type);
      if (filters.department) params.append('department', filters.department);

      const response = await axios.get(`${API}/careers/jobs?${params.toString()}`);
      setJobs(response.data.jobs);
      setPagination(prev => ({
        ...prev,
        total: response.data.total,
        total_pages: response.data.total_pages
      }));
    } catch (error) {
      console.error('Failed to fetch jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      location: '',
      work_mode: '',
      job_type: '',
      department: ''
    });
  };

  const formatWorkMode = (mode) => {
    const modes = {
      'remote': 'Remote',
      'hybrid': 'Hybrid',
      'onsite': 'On-site'
    };
    return modes[mode] || mode;
  };

  const formatJobType = (type) => {
    const types = {
      'fulltime': 'Full-time',
      'contract': 'Contract',
      'c2c': 'C2C'
    };
    return types[type] || type;
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-[#FFF4E6] to-[#FAFAF8] relative overflow-hidden">
        <div className="absolute inset-0 pattern-dots opacity-20" />
        <div className="container-custom relative z-10">
          <div className="max-w-3xl">
            <span className="text-[#F58B23] font-medium text-sm uppercase tracking-wider mb-4 block">
              Join Our Team
            </span>
            <h1 className="text-4xl md:text-5xl font-serif font-bold text-[#1A1A1A] mb-6">
              Build Your Career with TekClap
            </h1>
            <p className="text-lg text-[#6B7280] leading-relaxed mb-8">
              Join a team of passionate innovators who are shaping the future of technology 
              consulting. We offer challenging projects, continuous learning, and a culture 
              that celebrates excellence.
            </p>
            <div className="flex flex-wrap gap-6">
              <div className="flex items-center gap-2 text-[#6B7280]">
                <Users className="w-5 h-5 text-[#F58B23]" />
                <span>150+ Experts</span>
              </div>
              <div className="flex items-center gap-2 text-[#6B7280]">
                <MapPin className="w-5 h-5 text-[#F58B23]" />
                <span>Global Opportunities</span>
              </div>
              <div className="flex items-center gap-2 text-[#6B7280]">
                <Briefcase className="w-5 h-5 text-[#F58B23]" />
                <span>Diverse Projects</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Join TekClap */}
      <section className="py-16 bg-white">
        <div className="container-custom">
          <h2 className="text-2xl font-serif font-bold text-[#1A1A1A] mb-8 text-center">
            Why Join TekClap?
          </h2>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { icon: '🚀', title: 'Growth', desc: 'Continuous learning and career advancement opportunities' },
              { icon: '🌍', title: 'Global Reach', desc: 'Work with clients across North America, Europe, and Asia' },
              { icon: '💡', title: 'Innovation', desc: 'Work on cutting-edge AI and technology projects' },
              { icon: '🤝', title: 'Culture', desc: 'Collaborative environment with work-life balance' }
            ].map((item, index) => (
              <div key={index} className="text-center p-6 bg-[#FAFAF8] rounded-xl">
                <div className="text-3xl mb-3">{item.icon}</div>
                <h3 className="font-semibold text-[#1A1A1A] mb-2">{item.title}</h3>
                <p className="text-sm text-[#6B7280]">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Job Listings */}
      <section className="py-16 bg-[#FAFAF8]">
        <div className="container-custom">
          <h2 className="text-2xl font-serif font-bold text-[#1A1A1A] mb-8">
            Open Positions
          </h2>

          {/* Search and Filters */}
          <div className="bg-white rounded-xl p-6 mb-8 border border-gray-100">
            <div className="grid md:grid-cols-5 gap-4">
              {/* Search */}
              <div className="md:col-span-2 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search jobs..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="pl-10"
                />
              </div>

              {/* Location Filter */}
              <Select
                value={filters.location || "all"}
                onValueChange={(value) => handleFilterChange('location', value === "all" ? '' : value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Location" />
                </SelectTrigger>
                <SelectContent position="popper" sideOffset={4}>
                  <SelectItem value="all">All Locations</SelectItem>
                  {filterOptions.locations.map((loc) => (
                    <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Work Mode Filter */}
              <Select
                value={filters.work_mode || "all"}
                onValueChange={(value) => handleFilterChange('work_mode', value === "all" ? '' : value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Work Mode" />
                </SelectTrigger>
                <SelectContent position="popper" sideOffset={4}>
                  <SelectItem value="all">All Modes</SelectItem>
                  {filterOptions.work_modes.map((mode) => (
                    <SelectItem key={mode} value={mode}>{formatWorkMode(mode)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Job Type Filter */}
              <Select
                value={filters.job_type || "all"}
                onValueChange={(value) => handleFilterChange('job_type', value === "all" ? '' : value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Job Type" />
                </SelectTrigger>
                <SelectContent position="popper" sideOffset={4}>
                  <SelectItem value="all">All Types</SelectItem>
                  {filterOptions.job_types.map((type) => (
                    <SelectItem key={type} value={type}>{formatJobType(type)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Active filters indicator */}
            {(filters.search || filters.location || filters.work_mode || filters.job_type) && (
              <div className="mt-4 flex items-center gap-2">
                <span className="text-sm text-[#6B7280]">Active filters:</span>
                <button
                  onClick={clearFilters}
                  className="text-sm text-[#F58B23] hover:underline"
                >
                  Clear all
                </button>
              </div>
            )}
          </div>

          {/* Job List */}
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block w-8 h-8 border-4 border-[#F58B23] border-t-transparent rounded-full animate-spin" />
              <p className="mt-4 text-[#6B7280]">Loading positions...</p>
            </div>
          ) : jobs.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl border border-gray-100">
              <Briefcase className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-[#1A1A1A] mb-2">No positions found</h3>
              <p className="text-[#6B7280] mb-4">
                {filters.search || filters.location || filters.work_mode || filters.job_type
                  ? 'Try adjusting your filters'
                  : 'Check back soon for new opportunities'}
              </p>
              {(filters.search || filters.location || filters.work_mode || filters.job_type) && (
                <Button variant="outline" onClick={clearFilters}>
                  Clear Filters
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {jobs.map((job) => (
                <Link
                  key={job.job_id}
                  to={`/careers/${job.job_id}`}
                  className="block bg-white rounded-xl p-6 border border-gray-100 hover:border-[#F58B23] hover:shadow-md transition-all group"
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold text-[#1A1A1A] group-hover:text-[#F58B23] transition-colors mb-2">
                        {job.title}
                      </h3>
                      <div className="flex flex-wrap gap-4 text-sm text-[#6B7280]">
                        {job.department && (
                          <span className="flex items-center gap-1">
                            <Building2 className="w-4 h-4" />
                            {job.department}
                          </span>
                        )}
                        {(job.city || job.location) && (
                          <span className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            {job.city || job.location}
                          </span>
                        )}
                        {job.work_mode && (
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {formatWorkMode(job.work_mode)}
                          </span>
                        )}
                        {job.job_type && (
                          <span className="flex items-center gap-1">
                            <Briefcase className="w-4 h-4" />
                            {formatJobType(job.job_type)}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-[#F58B23] font-medium">
                      View Details
                      <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              ))}

              {/* Pagination */}
              {pagination.total_pages > 1 && (
                <div className="flex justify-center gap-2 mt-8">
                  <Button
                    variant="outline"
                    disabled={pagination.page === 1}
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  >
                    Previous
                  </Button>
                  <span className="flex items-center px-4 text-[#6B7280]">
                    Page {pagination.page} of {pagination.total_pages}
                  </span>
                  <Button
                    variant="outline"
                    disabled={pagination.page === pagination.total_pages}
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  >
                    Next
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Results count */}
          {!loading && jobs.length > 0 && (
            <p className="text-center text-sm text-[#6B7280] mt-6">
              Showing {jobs.length} of {pagination.total} positions
            </p>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#1A1A1A]">
        <div className="container-custom">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl font-serif font-bold text-white mb-4">
              Don't See the Right Fit?
            </h2>
            <p className="text-gray-400 mb-8">
              We're always looking for talented individuals. Send us your resume and we'll 
              keep you in mind for future opportunities.
            </p>
            <Link to="/contact">
              <Button className="bg-[#F58B23] hover:bg-[#D97706] text-white px-8">
                Get in Touch
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CareersPage;
